import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AgGridAngular } from 'ag-grid-angular';
import { DateTime } from 'luxon';
import * as _ from 'lodash';
import { AuthService } from '../../@services/auth.service';
import { UsersService } from '../../@services/users.service';
import { agGridThemes } from '../../@shared/ag-grid-themes';
import { ToastService } from '../../@services/toast.service';
import { SetFloatingFilter } from '../../@shared/set-floating-filter.component';
import { ScannersService } from '../../@services/scanners.service';
import { BillingService } from '../../@services/billing.service';
import { format_customer, format_date_string } from '../../@shared/helpers';

@Component({
  selector: 'app-user-activity',
  standalone : true,
  imports: [CommonModule, FormsModule, AgGridAngular],
  templateUrl: './user-activity.component.html',
  styleUrl: './user-activity.component.scss'
})
export class UserActivityComponent {
  user_email: string = '';

  gridDarkTheme = agGridThemes.dark;
  grid: any;
  data: any[] = [];
  filtered_data: any[] = [];
  theme: any;
  roles: any[] = [];
  customers: any[] = [];
  filtered_scanners: any[] = [];
  scanners: any[] = [];
  selected_event: any;
  profile: any = null;

  show_edit_user: boolean = false;
  updated_user: any = {
    profile: null,
    scanners: null,
    customers: null
  };

  constructor(
    private billingService : BillingService,
    private scannersService : ScannersService,
    private adminService : UsersService,
    private toast: ToastService
  ) {
    this.init_grid();
    this.theme = this.gridDarkTheme;
  }

  async ngOnInit() {
    await this.get_scanners();
    await this.get_roles();
    await this.get_customers();
  }

  ngOnDestroy(): void {}

  async get_roles() {
    let res = await this.adminService.getRoles();
    if (!res?.err) {
      this.roles = res;
    }
    else {
      this.roles = [];
    }
  }
  async get_customers() {
    let res = await this.adminService.getCustomers();
    if (!res?.err) {
      this.customers = res;
    }
    else {
      this.customers = [];
    }
  }
  async get_scanners() {
    this.scanners = [
      {Name: 'Strat Algo Alerts', Id: 'strat-algo'},
      {Name: "AlphaShark Alerts", Id: 'as-1'},
      {Name: "AlphaShark Plus Alerts", Id: 'as-2'},
      {Name: "Superior Options Alerts", Id: 'as-3'},
      {Name: "Superior Options Plus Alerts", Id: 'as-4'},
      {Name: "RSI Oversold", Id: 'as-5'},
      {Name: "Andrew's Personal Portfolio", Id: 'as-6'},
      {Name: "Weekly ATM Money Machine", Id: 'as-7'},
      {Name: "Bryan's Trade Alerts", Id: 'as-8'},
      {Name: "Bryan's Trade Plus Alerts", Id: 'as-9'},
      {Name: "Bryan's Institutional Trades", Id: 'as-10'},
      {Name: "AlphaShark Accelerator", Id: 'as-11'},
      {Name: "10/10", Id: 'as-12'},
      {Name: "Alpha Shark Scanner", Id: 'as-13'},
      {Name: "Petar 10K Challenge", Id: 'as-14'},
      {Name: "BK Alpha Alerts 360", Id: 'as-15'},
      {Name: "DeltaStrike Pro", Id: 'as-16'},
      {Name: "AlphaShark Bets", Id: 'as-17'},
      {Name: "AK's Stock Challenge", Id: 'as-18'},
      {Name: "MK's Earnings Plays Plus", Id: 'as-19'},
      {Name: "Strat Watchlists", Id: 'strat'},
      {Name: "Gapper Watchlists", Id: 'gappers'},
    ];

    let res = await this.scannersService.getScanners();
    if (res && !res.err) {
      this.scanners.push(...res);
      this.filtered_scanners = res;
    }
  }

  format_scanner(scanner: any) {
    let scannerName = scanner;
    const matchedScanner = this.scanners.find(s => s.Id === scanner);
    if (matchedScanner) scannerName = matchedScanner.Name;
    return scannerName;
  }

  async get_user_activity() {
    if (!this.user_email) return;

    let res: any = await this.adminService.getUserActivity(this.user_email);
    if (!res.err) {
      // Map user events to data array to display in grid
      this.data = res.events.map((event: any) => {
        // Format scanner name for each user event
        let scannerName = event.scanner;
        if (event.scanner) {
          const matchedScanner = this.scanners.find(s => s.Id === event.scanner);
          if (matchedScanner) scannerName = matchedScanner.Name;
        }

        // Format customer name for each user event
        let formattedCustomer = format_customer(event.customer);
        
        return {
          ...event,
          scanner: scannerName,
          customer: formattedCustomer
        };
      });

      // Extract profile data from response
      if (res.profile) {
        let res_scanners: any = res.profile.scanners ?? [];
        let user_scanners: any = res_scanners.map((s: any) => this.format_scanner(s));
        user_scanners.sort();

        let res_channels: any = res.profile.channels ?? [];
        let user_channels: any = res_channels.map((c: any) => this.format_scanner(c));
        user_channels.sort();
        
        // Customers storeds in Cognito
        let res_customers: any = res.profile.customers;

        // Customer tracking from Mongo
        let res_customer_tracking: any = res.profile.customer_tracking;

        // Verify that all customers from Cognito are added to tracking array in case of mismatches
        for (const customer of res_customers) {
          let exists = res_customer_tracking.some((obj: any) => obj.customer === customer);
          if (!exists) {
            res_customer_tracking.push({
              customer: customer,
              active: 'N/A',
            });
          }
        }

        // Create array with formatted customer name and status
        let user_customers: any = [];
        for (const customer of res_customer_tracking) {
          user_customers.push({
            name: format_customer(customer.customer),
            active: customer.active
          })
        }
        user_customers.sort((a: any, b: any) => a.name.localeCompare(b.name));

        let created = res.profile.created ? format_date_string(res.profile.created) : 'N/A';
        let updated = res.profile.updated ? format_date_string(res.profile.updated) : 'N/A';
        let last_login = res.profile.last_login ? format_date_string(res.profile.last_login) : 'N/A';

        this.profile = res.profile;
        this.profile.scanners = user_scanners;
        this.profile.channels = user_channels;
        this.profile.customers = user_customers;
        this.profile.created = created;
        this.profile.updated = updated;
        this.profile.last_login = last_login;

        this.updated_user.profile = res.profile;
        this.updated_user.scanners = res_scanners;
        this.updated_user.customers = res_customers;
      }
    }
  }

  on_user_scanner_change(scanner: any, event: any) {
    let selected = event.target.checked;
    if (selected) {
      this.updated_user.scanners.push(scanner);
    }
    else {
      const updated = this.updated_user.scanners.filter((s: any) => s !== scanner);
      this.updated_user.scanners = updated;
    }
  }

  on_user_customer_change(customer: any, event: any) {
    let selected = event.target.checked;
    if (selected) {
      this.updated_user.customers.push(customer);
    }
    else {
      const updated = this.updated_user.customers.filter((s: any) => s !== customer);
      this.updated_user.customers = updated;
    }
  }

  async update_user_attributes() {
    const username = this.updated_user.profile.username ?? null;
    const first_name = this.updated_user.profile.first_name;
    const last_name = this.updated_user.profile.last_name;
    const role = this.updated_user.profile.role;
    const live_data = this.updated_user.profile.live_data;
    const scanners = this.updated_user.scanners ?? [];
    const customers = this.updated_user.customers ?? [];
    scanners.sort();
    customers.sort();

    let data = {
      attributes: [
        {
          Name: 'custom:scanners',
          Value: scanners.join(',')
        },
        {
          Name: 'custom:customers',
          Value: customers.join(','),
        },
        {
          Name: 'custom:role',
          Value: role
        },
        {
          Name: 'custom:live_data_access',
          Value: live_data.toString()
        },
        {
          Name: 'name',
          Value: first_name
        },
        {
          Name: 'family_name',
          Value: last_name
        }
      ]
    };

    if (username) {
      this.show_edit_user = false;
      const res = await this.adminService.updateUserAttributes(username, data);
      if (res && !res.err) {
        this.toast.success('Attributes updated successfully.');
        await this.get_user_activity();
      }
    }
  }

  /**** User Activity Grid ****/
  init_grid() {
  
    this.grid = {
      options: {
        context: this,
        suppressCellFocus: true,
        rowSelection: {
          mode: 'singleRow',
          checkboxes: false,
          enableClickSelection: true,
        },
        defaultColDef: {
          floatingFilter: true,
          filter: true,
          cellStyle: { 'text-align': 'left'},
        },
        getRowId: (params: any) => {
          return params.data.id;
        },
        onRowClicked: this.on_row_clicked.bind(this),
      },
      statusBar: {
        statusPanels: [
          { statusPanel: 'agTotalRowCountComponent', align: 'left' },
          { statusPanel: 'agFilteredRowCountComponent', align: 'left' },
        ]
      },
      columnDefs: [
        {
          field: "date",
          headerName: 'Date',
          filter: 'agDateColumnFilter',
          width: 250,
          suppressSizeToFit: true,
          valueGetter: (params: any) => params.data['date'],
          valueFormatter: (params: any) => {
            if (!params.value) return '';
            const dt = DateTime.fromISO(params.value);
            return dt.toFormat('MM/dd/yyyy hh:mm:ss a');
          },
          comparator: (date1: string, date2: string) => {
            if (!date1 && !date2) return 0;
            if (!date1) return -1;
            if (!date2) return 1;
            const d1 = DateTime.fromISO(date1);
            const d2 = DateTime.fromISO(date2);
            return d1.toMillis() - d2.toMillis();
          },
          filterParams: {
            comparator: (filterLocalDateAtMidnight: Date, cellValue?: string) => {
              if (!cellValue) return -1;
              const cellDate = DateTime.fromISO(cellValue).startOf('day').toJSDate();
              return cellDate.getTime() - filterLocalDateAtMidnight.getTime();
            }
          }
        },
        {
          field: "desc",
          headerName: 'Description',
          minWidth: 150,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          field: "customer",
          headerName: 'Customer',
          minWidth: 100,
          floatingFilterComponent: SetFloatingFilter,
        },
        {
          field: "scanner",
          headerName: 'Item',
          minWidth: 100,
          floatingFilterComponent: SetFloatingFilter,
        }
      ]
    }
  }
  on_grid_ready(e: any) {
    // console.log('on_grid_ready()');
    this.grid.api = e.api;
    this.grid.colApi = e.colApi;
    this.grid.api.sizeColumnsToFit();
  }
  on_model_updated(e: any) {
    //Get the filtered rows from the grid
    if (this.grid?.api) {
      this.filtered_data = [];
      this.grid?.api.forEachNodeAfterFilter((node: any) => {
        this.filtered_data.push(node.data);
      });
    }
  }
  on_row_selected(e: any) {
    // console.log('on_row_selected', e);
    if (e?.node?.isSelected() === true) {
      this.selected_event = e.node.data;
    }

    //Force refresh of scanners column to show edit button
    this.grid.api.refreshCells({
      columns: ['attributes.custom:scanners'],
      force: true
    });
  }
  on_row_clicked(e: any) {
    if (e.event.target !== undefined) {
      let user = e.data;
      let action = e.event.target.getAttribute('data-button-action');
      switch (action) {
        case 'edit-scanners':
          //this.on_edit_user_scanners(user);
          break;
        default:
          break;
      }
    }
  }
  async get_scanner_values() {
    if (!this.scanners || this.scanners.length === 0) {
      await this.get_scanners();
    }

    if (!this.scanners.length) return [];

    return this.scanners.map((s: any) => {
      return s.Id;
    });
  }
}
